<script setup lang="ts">
  /**
   * Dark brand panel that sits beside the form on the unauthenticated pages
   * (register, login, forgot password). Purely presentational: a gradient
   * surface with a blueprint grid, a travelling highlight and a pulsing node,
   * plus the Bahnhof logo linking back to the public portal.
   *
   * The headline and lede come from the page via slots so each auth screen can
   * speak to what it actually does; the three trust points are brand constants
   * and stay here so every auth screen states them identically.
   */
  import { onMounted, ref } from 'vue'
  import logo from '@/assets/img/logo.svg'

  const logoImage = logo as string
  const portalLink = ref('')

  onMounted(() => {
    portalLink.value = import.meta.env.VITE_PORTAL_API_URL
  })
</script>

<template>
  <aside class="authentication-hero">
    <span class="authentication-hero__node" aria-hidden="true" />

    <a :href="portalLink" class="authentication-hero__logo">
      <img :src="logoImage" alt="Bahnhof" class="authentication-hero__logo-image" />
    </a>

    <div class="authentication-hero__body">
      <h1 class="authentication-hero__headline">
        <slot name="headline">Cloud with<br /><em>privacy</em>.</slot>
      </h1>

      <p class="authentication-hero__lead">
        <slot name="lede" />
      </p>

      <ul class="authentication-hero__point-list">
        <li class="authentication-hero__point">
          <svg
            class="authentication-hero__point-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="square"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M12 2 4 5v6c0 5 3.4 9.5 8 11 4.6-1.5 8-6 8-11V5z" />
          </svg>
          <span class="authentication-hero__point-text">Hosted in our Swedish underground data centres.</span>
        </li>

        <li class="authentication-hero__point">
          <svg
            class="authentication-hero__point-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="square"
            aria-hidden="true"
            focusable="false"
          >
            <polygon points="13 2 4 14 11 14 11 22 20 10 13 10" />
          </svg>
          <span class="authentication-hero__point-text">Powered by our European fibre network.</span>
        </li>

        <li class="authentication-hero__point">
          <svg
            class="authentication-hero__point-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="square"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="3" y="10" width="18" height="10" rx="1" />
            <path d="M3 10V9a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v1" />
            <line x1="3" y1="13" x2="21" y2="13" />
            <rect x="10.5" y="11" width="3" height="4" rx="0.5" />
          </svg>
          <span class="authentication-hero__point-text">Your data. Your control. No lock-ins.</span>
        </li>
      </ul>
    </div>
  </aside>
</template>

<style scoped lang="scss">
  /* Animatable so the highlight can travel across the grid. Registered here
     because the mask below interpolates them. */
  @property --bf-auth-hero-ripple-x {
    syntax: '<percentage>';
    inherits: true;
    initial-value: 20%;
  }

  @property --bf-auth-hero-ripple-y {
    syntax: '<percentage>';
    inherits: true;
    initial-value: 30%;
  }

  .authentication-hero {
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    align-self: start;
    height: 100vh;
    padding: var(--bf-sp-4) var(--bf-sp-5);
    overflow: hidden;
    isolation: isolate;
    color: var(--bf-fg-inverse-1);
    background:
      radial-gradient(ellipse at 88% 108%, rgba(234, 96, 10, 0.42) 0%, rgba(234, 96, 10, 0) 55%),
      linear-gradient(160deg, var(--bf-deep-blue) 0%, #0a2236 55%, var(--bf-dark-blue) 100%);
  }

  /* Static blueprint grid. */
  .authentication-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image:
      repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.055) 0 1px, transparent 1px 36px),
      repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.055) 0 1px, transparent 1px 36px);
  }

  /* Orange copy of the same grid, revealed only under a travelling soft mask. */
  .authentication-hero::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    opacity: 0.45;
    pointer-events: none;
    background-image:
      repeating-linear-gradient(0deg, rgba(240, 141, 0, 0.55) 0 1px, transparent 1px 36px),
      repeating-linear-gradient(90deg, rgba(240, 141, 0, 0.55) 0 1px, transparent 1px 36px);
    mask-image: radial-gradient(
      circle at var(--bf-auth-hero-ripple-x, 20%) var(--bf-auth-hero-ripple-y, 30%),
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.6) 8%,
      rgba(0, 0, 0, 0) 22%
    );
    animation: authentication-hero-ripple 11s linear infinite;
  }

  @keyframes authentication-hero-ripple {
    0% {
      --bf-auth-hero-ripple-x: 14%;
      --bf-auth-hero-ripple-y: 78%;
      opacity: 0;
    }

    8% {
      opacity: 0.55;
    }

    25% {
      --bf-auth-hero-ripple-x: 38%;
      --bf-auth-hero-ripple-y: 22%;
      opacity: 0.6;
    }

    50% {
      --bf-auth-hero-ripple-x: 72%;
      --bf-auth-hero-ripple-y: 58%;
      opacity: 0.5;
    }

    75% {
      --bf-auth-hero-ripple-x: 24%;
      --bf-auth-hero-ripple-y: 44%;
      opacity: 0.55;
    }

    92% {
      opacity: 0.5;
    }

    100% {
      --bf-auth-hero-ripple-x: 14%;
      --bf-auth-hero-ripple-y: 78%;
      opacity: 0;
    }
  }

  .authentication-hero__node {
    position: absolute;
    right: 18%;
    bottom: 26%;
    z-index: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--bf-orange-light);
    box-shadow: 0 0 12px rgba(240, 141, 0, 0.7);
  }

  .authentication-hero__node::before,
  .authentication-hero__node::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1.5px solid var(--bf-orange-light);
    border-radius: 50%;
    animation: authentication-hero-node-pulse 3.2s var(--bf-ease) infinite;
  }

  .authentication-hero__node::after {
    animation-delay: 1.6s;
  }

  @keyframes authentication-hero-node-pulse {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }

    70% {
      transform: scale(7);
      opacity: 0;
    }

    100% {
      transform: scale(7);
      opacity: 0;
    }
  }

  .authentication-hero__logo {
    position: relative;
    z-index: 1;
    display: block;
    width: fit-content;
    border-radius: var(--bf-r-sm);
  }

  .authentication-hero__logo:focus-visible {
    outline: 2px solid var(--bf-orange-light);
    outline-offset: 4px;
  }

  /* The logo asset is a raster mark with dark ink, so it is inverted to read
     on the dark panel rather than shipping a second file. */
  .authentication-hero__logo-image {
    display: block;
    width: auto;
    height: 60px;
    filter: brightness(0) invert(1);
  }

  .authentication-hero__body {
    position: relative;
    z-index: 1;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    max-width: 540px;
  }

  .authentication-hero__headline {
    margin: 0 0 var(--bf-sp-3);
    font-family: var(--bf-font-display);
    font-size: clamp(var(--bf-fs-3xl), 5.5vw, 72px);
    font-weight: var(--bf-fw-bold);
    line-height: 0.96;
    letter-spacing: -0.035em;
    color: var(--bf-fg-inverse-1);
    text-wrap: balance;
  }

  .authentication-hero__headline :deep(em) {
    font-style: italic;
    font-weight: var(--bf-fw-bold);
    color: var(--bf-orange-light);
  }

  .authentication-hero__lead {
    max-width: 440px;
    margin: 0 0 var(--bf-sp-4);
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-md);
    line-height: var(--bf-lh-normal);
    color: var(--bf-fg-inverse-2);
  }

  .authentication-hero__point-list {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-2);
    max-width: 440px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .authentication-hero__point {
    display: flex;
    gap: var(--bf-sp-2);
    align-items: flex-start;
    padding: var(--bf-sp-2) 0;
  }

  .authentication-hero__point-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--bf-orange-light);
  }

  .authentication-hero__point-text {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-bold);
    letter-spacing: -0.005em;
    color: var(--bf-fg-inverse-1);
  }

  @media (max-width: 880px) {
    .authentication-hero {
      position: relative;
      height: auto;
      min-height: 300px;
      padding: var(--bf-sp-3) var(--bf-sp-4);
    }

    .authentication-hero__point-list {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .authentication-hero::after,
    .authentication-hero__node::before,
    .authentication-hero__node::after {
      animation: none;
    }
  }
</style>

<!-- Deliberately unscoped, like BfConsoleShell's: a scoped `:root:has(…)` could never match. -->
<style lang="scss">
  /* --toast-viewport-inset-start — every split auth page lays the hero and the form out as
     `1.05fr 1fr` across the full window, so the form column starts at 1.05 / 2.05 of it. The
     global toast stack centres on that column instead of on the window. Change it together
     with the pages' grid. */
  :root:has(.authentication-hero) {
    --toast-viewport-inset-start: calc(100% * 1.05 / 2.05);
  }

  @media (max-width: 880px) {
    /* The pages stack to one column: the form spans the window again */
    :root:has(.authentication-hero) {
      --toast-viewport-inset-start: 0px;
    }
  }
</style>
